// TrackFiji V3.3.0 — per-device map display: icon, icon colour and the trail ("tail") behind the marker.
// Saved for everyone in the company; editable by Skyward staff and the company owner.
import { TAIL_SECONDS, tailLabel, tailSec } from "../../lib/tail";
import { useState } from "react";
import type { Device } from "../../lib/supabase";
import { orgApi } from "../../lib/org";
import { ICONS, iconSvg, normIcon } from "../../lib/icons";
import { silhouetteSvg } from "../../lib/vehicleIcons";
import { Drawer, Field } from "../admin/ui";
import ColourPicker, { rememberColour } from "../../ui/ColourPicker";

export type DisplayPatch = Pick<Device, "icon" | "icon_color" | "tail_on" | "tail_minutes" | "tail_seconds" | "tail_color">;

export default function DeviceDisplayDrawer({ device, onClose, onSaved }: {
  device: Device; onClose: () => void; onSaved: (d: DisplayPatch) => void;
}) {
  const [icon, setIcon] = useState(normIcon(device.icon));
  const [iconColor, setIconColor] = useState<string | null>(device.icon_color ?? null);
  const [tailOn, setTailOn] = useState(device.tail_on ?? true);
  const [tailS, setTailS] = useState(() => { const s = tailSec(device); return TAIL_SECONDS.includes(s) ? s : 600; });
  const [tailColor, setTailColor] = useState<string | null>(device.tail_color ?? null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true); setErr(null);
    try {
      const saved = await orgApi<DisplayPatch>("PATCH", `devices/${device.id}/display`,
        { icon, icon_color: iconColor, tail_on: tailOn, tail_seconds: tailS, tail_color: tailColor });
      [iconColor, tailColor].forEach((c) => c && rememberColour(c));
      onSaved(saved);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }


  return (
    <Drawer title={`Icon & tail · ${device.name}`} onClose={onClose}
      footer={<><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</button></>}>
      <div className="aform">
        <div className="wide dd-preview" aria-hidden="true">
          <svg viewBox="0 0 220 60" className="dd-tail">
            {tailOn && [0, 1, 2, 3, 4].map((i) => (
              <line key={i} x1={20 + i * 30} y1={44 - i * 4} x2={50 + i * 30} y2={40 - i * 4}
                stroke={tailColor ?? iconColor ?? "#257AF4"} strokeWidth="4" strokeLinecap="round" opacity={0.15 + i * 0.18} />
            ))}
          </svg>
          <span className="mk" data-status="moving" data-custom={iconColor ? "true" : "false"} style={iconColor ? ({ "--mk-bg": iconColor } as React.CSSProperties) : undefined}>
            <span className="mk-dot" dangerouslySetInnerHTML={{ __html: silhouetteSvg(icon, 36, Number(device.state?.course ?? 0)) }} />
            <span className="mk-label">{device.name}</span>
          </span>
        </div>
        <Field label="Icon" wide>
          <div className="icon-grid" role="group">
            {Object.entries(ICONS).map(([k, v]) => (
              <button key={k} type="button" className={icon === k ? "on" : ""} onClick={() => setIcon(k)} title={v.hint ? `${v.label} — ${v.hint}` : v.label}>
                <span dangerouslySetInnerHTML={{ __html: iconSvg(k, 18) }} /><small>{v.label}</small>
              </button>
            ))}
          </div>
        </Field>
        <Field label="Icon colour" wide hint="Auto colours the icon by status (green moving, amber stopped, red offline). With a fixed colour the status shows as a ring.">
          <ColourPicker value={iconColor} onChange={setIconColor} allowAuto />
        </Field>
        <Field label="Tail" wide hint="A fading line behind the marker showing where it has just been.">
          <span className="check"><input type="checkbox" checked={tailOn} onChange={(e) => setTailOn(e.target.checked)} /> Show tail on the live map</span>
        </Field>
        {tailOn && <>
          <Field label="Tail length">
            <select value={tailS} onChange={(e) => setTailS(Number(e.target.value))}>
              {TAIL_SECONDS.map((s) => <option key={s} value={s}>Last {tailLabel(s)}</option>)}
            </select>
          </Field>
          <Field label="Tail colour" wide><ColourPicker value={tailColor} onChange={setTailColor} allowAuto /></Field>
        </>}
        <p className="wide sub" style={{ margin: 0 }}>These settings apply for everyone in the company.</p>
        {err && <div className="wide error">{err}</div>}
      </div>
    </Drawer>
  );
}
